import { UNIFORM_LIMITS } from "./entities.js";
/**
 * O link do cliente (F232).
 *
 * A atendente gera um link; o cliente preenche a grade (e o cadastro, se não
 * tem) e envia; a atendente confere e só então nasce o pedido. A mesma lista
 * de situações mora no contrato e no CHECK da 0120.
 */
export const INTAKE_STATUSES = [
    'aberto',
    'enviado',
    'convertido',
    'cancelado',
];
export const isIntakeStatus = (value) => typeof value === 'string' &&
    INTAKE_STATUSES.includes(value);
export const intakeView = (status, expiresAt, now) => status === 'aberto' && expiresAt.getTime() <= now.getTime()
    ? 'expirado'
    : status;
/** O cliente ainda pode enviar? Só o aberto e dentro da validade. */
export const acceptsSubmission = (status, expiresAt, now) => intakeView(status, expiresAt, now) === 'aberto';
/**
 * Os acabamentos que o link oferece de um tecido: os que a atendente deixou,
 * entre os que o tecido ainda tem. Sem escolha (ou se a escolha ficou sem
 * nenhum, porque a loja mudou o tecido), todos os do tecido.
 */
export const linkFinishes = (chosen, fabricId, finishes) => {
    const own = chosen.find((entry) => entry.fabricId === fabricId)?.finishes;
    const kept = finishes.filter((finish) => own?.includes(finish) === true);
    return kept.length > 0 ? kept : finishes;
};
/**
 * O que o cliente pediu, em conjuntos e peças: a peça com duas partes ou mais
 * é conjunto, e conta pela primeira parte (o envio já conferiu que batem); a
 * peça de uma parte só, mesmo preenchida por partes, é peça.
 */
export const intakeTally = (items) => {
    let sets = 0;
    let pieces = 0;
    for (const item of items) {
        const kit = item.kit;
        if (kit == null) {
            pieces += item.rows.reduce((sum, row) => sum + row.quantity, 0);
            continue;
        }
        const parts = new Set([
            ...kit.people.flatMap((person) => person.pieces.map((p) => p.partId)),
            ...kit.counts.map((count) => count.partId),
        ]);
        const first = kit.people[0]?.pieces[0]?.partId ?? kit.counts[0]?.partId;
        const total = kit.people.reduce((sum, person) => sum + person.quantity, 0) +
            kit.counts
                .filter((count) => count.partId === first)
                .reduce((sum, count) => sum + count.quantity, 0);
        if (parts.size > 1)
            sets += total;
        else
            pieces += total;
    }
    return { sets, pieces };
};
/** Quantos dias um link vale, no máximo e por padrão. */
export const INTAKE_LIMITS = {
    defaultDays: 7,
    maxDays: 60,
    /** Peças diferentes num envio. */
    items: 30,
    /** Linhas somadas de todas as peças: a mesma conta dos personalizados. */
    rows: UNIFORM_LIMITS.personalizations,
};
/** O nome do personalizado que só tem número. */
export const UNNAMED_PERSON = '—';
/** A cor da linha da grade que ficou sem cor (o mesmo traço da ficha). */
export const NO_COLOR = '—';
const cut = (text, max) => text.trim().replace(/\s+/g, ' ').slice(0, max).trim();
/**
 * O nome e o número de uma peça do conjunto (F300), já cortados; nulo quando
 * ela não leva nenhum dos dois. Só o número leva o traço no lugar do nome.
 */
const personalOf = (piece) => {
    const name = piece.name === null ? '' : cut(piece.name, UNIFORM_LIMITS.personName);
    const number = piece.number === null ? '' : cut(piece.number, UNIFORM_LIMITS.personNumber);
    return name === '' && number === ''
        ? null
        : { name: name === '' ? UNNAMED_PERSON : name, number };
};
/**
 * Quantos conjuntos tem o item (F300): o total de cada parte, que precisa ser
 * o mesmo em todas. Nulo quando as partes não batem (10 camisas e 9 shorts)
 * ou não há peça nenhuma.
 */
export const kitCount = (partIds, kit) => {
    const people = kit.people.reduce((sum, person) => sum + person.quantity, 0);
    const totals = partIds.map((partId) => people +
        kit.counts
            .filter((count) => count.partId === partId)
            .reduce((sum, count) => sum + count.quantity, 0));
    const first = totals[0] ?? 0;
    return first > 0 && totals.every((total) => total === first) ? first : null;
};
/**
 * As peças personalizadas do conjunto, uma linha por peça (F300): é o que a
 * personalização cobra — "por peça", então camisa e shorts com o número são
 * duas.
 */
export const kitPersonalizedRows = (kit) => kit.people.flatMap((person) => person.pieces.flatMap((piece) => {
    const personal = personalOf(piece);
    return personal === null
        ? []
        : [
            {
                size: piece.size,
                quantity: person.quantity,
                name: personal.name === UNNAMED_PERSON ? null : personal.name,
                number: personal.number === '' ? null : personal.number,
            },
        ];
}));
/**
 * O que o cliente preencheu, na forma da ficha.
 *
 * A linha com nome vira personalizado (nome, número, tamanho); a sem nome vai
 * para a grade, somada por tamanho — é como a planilha da loja separa. O
 * produto dá o nome da peça (e do modelo, na grade): cada produto é uma linha
 * da grade, na ordem em que o cliente os pôs.
 *
 * O conjunto com partes (F300) vira as peças dele: cada tipo de peça é a sua
 * linha da grade ("Camiseta babylook", "Shorts feminino"), cada peça com nome
 * ou número vai personalizada — a sem, para a grade, no tamanho dela —, e a
 * linha do pedido conta os conjuntos.
 */
export const intakeToSheet = (items) => {
    const grade = [];
    const personalization = [];
    const quantities = [];
    let position = 0;
    for (const item of items) {
        const block = item.color == null
            ? NO_COLOR
            : cut(item.color, UNIFORM_LIMITS.block) || NO_COLOR;
        // As peças da grade deste item, na ordem em que aparecem, por tamanho.
        const lines = new Map();
        const toGrade = (piece, size, quantity) => {
            const bySize = lines.get(piece) ?? new Map();
            bySize.set(size, (bySize.get(size) ?? 0) + quantity);
            lines.set(piece, bySize);
        };
        let total = 0;
        if (item.kit != null) {
            const kit = item.kit;
            for (const person of kit.people) {
                total += person.quantity;
                for (const part of person.pieces) {
                    const piece = cut(part.piece, UNIFORM_LIMITS.piece) || 'Peça';
                    const personal = personalOf(part);
                    if (personal === null)
                        toGrade(piece, part.size, person.quantity);
                    else
                        personalization.push({
                            ...personal,
                            piece,
                            size: part.size,
                            quantity: person.quantity,
                        });
                }
            }
            for (const count of kit.counts) {
                toGrade(cut(count.piece, UNIFORM_LIMITS.piece) || 'Peça', count.size, count.quantity);
            }
            // Os conjuntos: a primeira parte conta (o envio já conferiu que batem).
            const firstPart = kit.people[0]?.pieces[0]?.partId ?? kit.counts[0]?.partId;
            total += kit.counts
                .filter((count) => count.partId === firstPart)
                .reduce((sum, count) => sum + count.quantity, 0);
        }
        else {
            const piece = cut(item.productName, UNIFORM_LIMITS.piece) || 'Peça';
            for (const row of item.rows) {
                total += row.quantity;
                const name = row.name === null ? '' : cut(row.name, UNIFORM_LIMITS.personName);
                const number = row.number === null
                    ? ''
                    : cut(row.number, UNIFORM_LIMITS.personNumber);
                if (name === '' && number === '') {
                    toGrade(piece, row.size, row.quantity);
                }
                else {
                    // Só o número também é personalizado (e paga a personalização, F258):
                    // na grade ele se perderia. O personalizado pede um nome; o traço diz
                    // "sem nome".
                    personalization.push({
                        name: name === '' ? UNNAMED_PERSON : name,
                        piece,
                        number,
                        size: row.size,
                        quantity: row.quantity,
                    });
                }
            }
        }
        // Cada peça é uma linha da grade (a mesma linha não repete tamanho).
        for (const [piece, bySize] of lines) {
            for (const [size, quantity] of bySize) {
                grade.push({ block, piece, size, quantity, position });
            }
            position += 1;
        }
        quantities.push(total);
    }
    return { grade, personalization, quantities };
};
/** "Maria Clara Souza" → "Maria". Para a saudação da página, sem expor o resto. */
export const firstName = (name) => name.trim().split(/\s+/)[0] ?? '';
// ---------------------------------------------------------------------------
// Para quando (F233)
// ---------------------------------------------------------------------------
/**
 * O prazo de produção padrão do link, em dias depois do envio — "a gente faz
 * o cálculo dos dias, 30 dias por exemplo" (o dono, em 05/10). A atendente
 * troca em cada link.
 */
export const DEFAULT_LEAD_DAYS = 30;
/** Para evento, o despacho é uma semana antes da data dele. */
export const EVENT_LEAD_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1_000;
/** `AAAA-MM-DD` mais (ou menos) dias de calendário — meio-dia UTC, sem horário de verão. */
export const addDays = (day, days) => new Date(new Date(`${day}T12:00:00Z`).getTime() + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
/**
 * A data de despacho que o link calcula (F233).
 *
 * Evento: uma semana antes da data do evento. Sem evento: o dia do envio mais
 * o prazo de produção do link. A atendente confere e muda na ficha.
 */
export const intakeDispatchDate = (input) => input.eventDate === null
    ? addDays(input.submittedOn, input.leadDays)
    : addDays(input.eventDate, -EVENT_LEAD_DAYS);
/** O despacho do evento cai antes de hoje + 1: prazo curto, a atendente precisa ver. */
export const isTightEvent = (eventDate, today) => addDays(eventDate, -EVENT_LEAD_DAYS) <= today;
//# sourceMappingURL=intake.js.map